/**
 * Login Component
 *
 * This component renders a login form with support for password reset.
 * It includes reusable input and button components for optimization and maintains UI state using props.
 *
 * Props:
 * @param {string} email - The user's email input.
 * @param {Function} setemail - Function to update the email state.
 * @param {string} password - The user's password input.
 * @param {Function} setPassword - Function to update the password state.
 * @param {string} phone - The user's phone number input.
 * @param {Function} setphone - Function to update the phone state.
 * @param {Object|null} error - Object containing error information (code, message, and optional field-specific errors).
 * @param {boolean} loading - Whether the form is in a loading state.
 * @param {Function} handleLogin - Function to handle the login action.
 * @param {boolean} forgotPassword - Whether the password reset form is displayed.
 * @param {Function} setForgotPassword - Function to toggle the forgot password state.
 * @param {string} resetEmail - The email input for password reset.
 * @param {Function} setResetEmail - Function to update the reset email state.
 * @param {Function} handleForgotPassword - Function to handle the forgot password action.
 * @param {boolean} otpSent - Whether OTP has been sent (used in phone login flow).
 * @param {Function} setOtpSent - Function to update the OTP sent state.
 * @param {string|null} popupMessage - Message displayed in a temporary popup (e.g., after sending OTP).
 * @param {string} activeTab - Current active login tab (email or phone).
 * @param {Function} setActiveTab - Function to switch between login tabs.
 * @param {Function} handleVerifyOtp - Function to handle OTP verification.
 * @param {string} otp - Current OTP entered by the user.
 * @param {Function} setOtp - Function to update the OTP input state.
 */
import React, { memo, useState } from "react"
import lockerImg from "../../asset/locker_img.jpeg"
import logo from "../../asset/logo.png"
import { ServerError } from "../../infrastructure/models/enums/servererror"
import { LocalError } from "../../infrastructure/models/enums/localerror"
import { TYPES } from "../models/types"
import ActionButton from "./button/ActionButton"
import TabButton from "./button/TabButton"
import WebsiteButton from "./button/WebsiteButton"
import ForgotPasswordButton from "./button/ForgotPasswordButton"
import PasswordEyeButton from "./button/PasswordEyeButton"
import { COLORS } from "../../presentation/constants/colors"
import { ActionButtonVariant } from "../models/enum/ActionButtonVariant"

/**
 * InputField Component
 *
 * A reusable and memoized input field with optional password visibility toggle and error display.
 */
interface InputFieldProps {
  label: string
  type: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  disabled: boolean
  placeholder: string
  error?: string
}

const InputField: React.FC<InputFieldProps> = memo(
  ({ label, type, value, onChange, disabled, placeholder, error }) => {
    const [showPassword, setShowPassword] = useState(false)
    const isPasswordField = type === TYPES.PASSWORD
    const id = label.replace(/\s+/g, "-").toLowerCase()

    return (
      <div className="relative">
        <label
          htmlFor={id}
          className="font-bold text-sm text-gray-600 pb-1 block"
        >
          {label}
        </label>
        <input
          id={id}
          type={isPasswordField && !showPassword ? TYPES.PASSWORD : TYPES.TEXT}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          className={`border rounded-lg px-3 py-2 mt-1 text-sm w-full pr-10 ${
            error
              ? "border-red-500 mb-1 ring-1 ring-red-500"
              : "mb-5 border-gray-300"
          } focus:outline-none focus:ring-2 ${
            error ? "focus:ring-red-500" : "focus:ring-blue-500"
          }`}
        />
        {isPasswordField && (
          <PasswordEyeButton
            show={showPassword}
            onToggle={() => setShowPassword(!showPassword)}
            id=""
          />
        )}
        {error && <p className="text-red-500 text-sm mt-1 mb-2">{error}</p>}
      </div>
    )
  }
)

/**
 * Props for Login Component
 */
interface LoginProps {
  email: string
  setemail: (value: string) => void
  password: string
  setPassword: (value: string) => void
  phone: string
  setPhone: (value: string) => void
  error: {
    code: string
    message: string
    details?: {
      email?: string
      password?: string
    }
  } | null
  loading: boolean
  handleLogin: () => void
  forgotPassword: boolean
  setForgotPassword: (value: boolean) => void
  resetEmail: string
  setResetEmail: (value: string) => void
  handleForgotPassword: () => void
  otpSent: boolean
  setOtpSent: (val: boolean) => void
  popupMessage: string | null
  activeTab: string
  setActiveTab: (val: string) => void
  handleVerifyOtp: () => void
  otp: string
  setOtp: (val: string) => void
}

/**
 * Login
 *
 * Main login component supporting both email/password and phone/OTP login.
 */
const Login: React.FC<LoginProps> = ({
  email,
  setemail,
  password,
  setPassword,
  phone,
  setPhone,
  error,
  loading,
  handleLogin,
  forgotPassword,
  setForgotPassword,
  resetEmail,
  setResetEmail,
  handleForgotPassword,
  otpSent,
  popupMessage,
  activeTab,
  setActiveTab,
  handleVerifyOtp,
  otp,
  setOtp,
}) => {
  const [rememberMe, setRememberMe] = useState(false)
  // Extract specific error types for UI display
  let emailError = ""
  let forgotemailError = ""
  let passwordError = ""
  let errorMessage = ""

  if (error) {
    if (error.details) {
      emailError = error.details.email || ""
      passwordError = error.details.password || ""
    } else if (error.code) {
      switch (error.code) {
        case ServerError.USER_NOT_FOUND:
          if (forgotPassword) {
            forgotemailError = error.message
          } else {
            emailError = error.message
          }
          break
        case ServerError.INVALID_CREDENTIALS:
          passwordError = error.message
          break
        case LocalError.NETWORK_ERROR:
        case LocalError.SERVER_DOWN:
        case LocalError.TEMPORARY_UNAVAILABLE:
        case LocalError.UNEXPECTED_ERROR:
          errorMessage = LocalError.getDescription(error.code)
          break
        default:
          console.error(`Unhandled error code: ${error.code}`, error.message)
          errorMessage =
            error.message ||
            LocalError.getDescription(LocalError.UNEXPECTED_ERROR)
          break
      }
    }
  }
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
          <div className="text-white text-lg font-semibold">Loading....</div>
        </div>
      )}
      {/** OTP Pup-up */}
      {popupMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/20">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96 transform transition-all">
            <div className="flex flex-col items-center">
              {/* Green tick icon */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-green-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-gray-600 text-center">{popupMessage}</p>
            </div>
          </div>
        </div>
      )}
      {/* Left Image Panel */}
      <div
        className="relative w-full lg:w-1/2 flex-shrink-0 flex flex-col text-white p-6 lg:p-10 lg:pl-16 lg:pr-16"
        style={{
          backgroundImage: `url(${lockerImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "300px",
        }}
      >
        <div
          className="absolute inset-0 opacity-90"
          style={{ backgroundColor: COLORS.DARK_BLUE }}
        ></div>
        <div className="relative z-10 flex flex-col h-full">
          <div className="pt-4 lg:ml-16 flex justify-center lg:justify-start">
            <img
              src={logo}
              alt="Zibomo Logo"
              className="bg-white p-3 rounded-md"
              style={{ width: "180px" }}
            />
          </div>
          <div className="flex flex-col justify-center flex-grow max-w-lg mx-auto px-4 lg:px-0">
            <h1 className="text-2xl lg:text-4xl font-bold mb-4 text-center lg:text-left">
              Smart Lockers for a modern world
            </h1>
            <p className="text-sm opacity-90 mb-6 text-center lg:text-left">
              Zibomo Smart Lockers is the best solution to transform
              time-consuming manual tasks into automated processes...
            </p>
            <div className="flex justify-center lg:justify-start">
              <WebsiteButton
                url="https://www.zibomo.ooo"
                label="Visit Website"
                className="border-white text-white hover:text-[#1a2142] transition-colors"
                id=""
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right Login Form Panel */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-md">
          <h2 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-6 lg:mb-8 text-center">
            Vendor Login
          </h2>

          {!forgotPassword ? (
            <>
              <div
                className="flex border-b border-gray-200 mb-6"
                role="tablist"
              >
                <TabButton
                  label="Email"
                  isActive={activeTab === TYPES.EMAIL}
                  onClick={() => setActiveTab(TYPES.EMAIL)}
                  id="email-tab"
                />
                <TabButton
                  label="Phone Number"
                  isActive={activeTab === TYPES.PHONE}
                  onClick={() => setActiveTab(TYPES.PHONE)}
                  id="phone-tab"
                />
              </div>
              {/* Login Form */}
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleLogin()
                }}
              >
                {activeTab === TYPES.EMAIL ? (
                  <>
                    <InputField
                      label="Email Id"
                      type={TYPES.EMAIL}
                      value={email}
                      onChange={(e) => setemail(e.target.value)}
                      disabled={loading}
                      placeholder="Enter Email Id"
                      error={emailError}
                    />
                    <InputField
                      label="Password"
                      type={TYPES.PASSWORD}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      placeholder="Enter Password"
                      error={passwordError}
                    />
                    <ActionButton
                      variant={ActionButtonVariant.PRIMARY}
                      onClick={handleLogin}
                      label={loading ? "Loading..." : "SUBMIT"}
                      disabled={loading}
                      id="email-submit-button"
                    />
                  </>
                ) : (
                  <>
                    <div className="relative mb-5">
                      <label className="font-bold text-sm text-gray-600 pb-1 block">
                        Phone No
                      </label>
                      <div className="flex">
                        <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-700">
                          +91
                        </span>
                        <input
                          type="tel"
                          value={phone.replace("+91", "")}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "")
                            setPhone("+91" + raw)
                          }}
                          disabled={loading || otpSent}
                          placeholder="Enter Phone Number"
                          className={`border rounded-r-lg px-3 py-2 text-sm w-full ${
                            emailError
                              ? "border-red-500 ring-1 ring-red-500"
                              : "border-gray-300"
                          } focus:outline-none focus:ring-2 ${
                            emailError
                              ? "focus:ring-red-500"
                              : "focus:ring-blue-500"
                          }`}
                          inputMode="numeric"
                          pattern="[0-9]*"
                        />
                      </div>
                      {emailError && (
                        <p className="text-red-500 text-sm mt-1">
                          {emailError}
                        </p>
                      )}
                    </div>

                    {otpSent && (
                      <div className="relative mb-4">
                        <div className="flex flex-col gap-2">
                          <label className="font-bold text-sm text-gray-600">
                            OTP
                          </label>
                          <div className="flex justify-start gap-2">
                            {[...Array(6)].map((_, index) => (
                              <input
                                key={index}
                                type="text"
                                maxLength={1}
                                value={otp[index] || ""}
                                onChange={(e) => {
                                  const val = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  )
                                  const newOtp = otp.split("")
                                  newOtp[index] = val
                                  setOtp(newOtp.join(""))
                                  if (val && index < 5) {
                                    const next = document.getElementById(
                                      `otp-${index + 1}`
                                    )
                                    next?.focus()
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "Backspace" &&
                                    !otp[index] &&
                                    index > 0
                                  ) {
                                    const prev = document.getElementById(
                                      `otp-${index - 1}`
                                    )
                                    prev?.focus()
                                  }
                                }}
                                id={`otp-${index}`}
                                className="w-10 h-12 border border-gray-300 rounded-md text-center text-lg font-semibold focus:ring focus:ring-red-400"
                                disabled={loading}
                                inputMode="numeric"
                                pattern="[0-9]*"
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <ActionButton
                      id="otp-button"
                      variant={ActionButtonVariant.PRIMARY}
                      onClick={otpSent ? handleVerifyOtp : handleLogin}
                      label={otpSent ? "SUBMIT" : "Generate OTP"}
                      disabled={loading}
                    />
                  </>
                )}
              </form>
              {/* Remember & Forgot Password */}
              <div className="mt-6 flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
                <ForgotPasswordButton
                  onClick={() => setForgotPassword(true)}
                  disabled={loading}
                  id="forgot-password"
                />
              </div>
            </>
          ) : (
            <>
              {/* Password Reset Form */}
              <h3 className="text-xl font-medium text-gray-900 mb-4">
                Reset Password
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Enter your email address and we'll send you a link to reset your
                password.
              </p>
              <InputField
                label="Email"
                type={TYPES.EMAIL}
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={loading}
                placeholder="Enter your email"
                error={forgotemailError}
              />
              <ActionButton
                variant={ActionButtonVariant.PRIMARY}
                onClick={handleForgotPassword}
                label="Send Reset Link"
                disabled={loading}
                id="send-reset-link"
              />
              <div className="mt-4">
                <ActionButton
                  variant={ActionButtonVariant.BLUE}
                  onClick={() => setForgotPassword(false)}
                  label="Back to Login"
                  disabled={loading}
                  id="back-to-login"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Login